import { Box } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllInvoiceAddress } from 'src/shared/services/invoiceAddressService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceAddressResponse } from '../models/invoiceAddressResponse';
import { InvoiceAddressSearchFilter } from '../models/invoiceAddressSearchFilter';
import { InvoiceAddressEditModal } from './invoiceAddressEditModal';

type InvoiceAddressDataGridProps = {
  searchFilter: InvoiceAddressSearchFilter;
};

export const InvoiceAddressDataGrid = (props: InvoiceAddressDataGridProps) => {
  const { searchFilter } = props;
  const { t } = useTranslation(['association']);
  const isMounted = useIsMounted();
  const { dateStamp } = useGlobals();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [invoiceAddressId, setInvoiceAddressId] = useState<number>();

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);
  const [rows, setRows] = useState<InvoiceAddressResponse[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize || DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const result = await getAllInvoiceAddress(searchFilter);
      if (isMounted()) {
        const associationsResult = result.data.results.map((x) => {
          return { ...x, id: x.invoiceAddress_id };
        });
        ConsoleHelper.log(associationsResult, dateStamp);
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          setRows(associationsResult);
        }, 100);
      }
    } catch (error) {
      if (isMounted()) {
        updateData('loading', false);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    setEditMode(true);
    setInvoiceAddressId(params.row.invoiceAddress_id);
    ConsoleHelper.log('row click', params.row.invoiceAddress_id, params);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('association:tableHeaders.name'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.name || ''}</>;
      },
    },
    {
      field: 'invoiceAddress_id',
      headerName: t('association:tableHeaders.name'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.invoiceAddress_id || ''}</>;
      },
    },
  ];

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-cell': {
            maxHeight: 'fit-content !important',
            overflow: 'auto',
            lineHeight: '16px !important',
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
            display: 'flex !important',
            alignItems: 'flex-start !important',
          },
          '& .MuiDataGrid-viewport, & .MuiDataGrid-row, & .MuiDataGrid-renderingZone': {
            maxHeight: 'fit-content !important',
          },
        }}
        ref={scrollRef}
      >
        <DataGrid<InvoiceAddressResponse>
          autoHeight
          onRowClick={onRowClick}
          columns={columns}
          rows={rows}
          dataModel={data}
          sortModel={sortModel}
          checkboxSelection={false}
          onSelectionModelChange={() => ConsoleHelper.log()}
          selectionModel={[]}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length) {
              setSortModel(newSortModel);
            } else {
              setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
            }
          }}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </Box>

      {editMode && invoiceAddressId && (
        <InvoiceAddressEditModal modalOpen={editMode} invoiceAddress_id={invoiceAddressId} onClose={() => setEditMode(false)} />
      )}
    </>
  );
};
